import type { EnvVisibility } from '@hangar/shared';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Label } from '@/components/ui/label';
import { VISIBILITY_HELP } from './envHelpers';
import { VISIBILITIES, visibilityLocked } from './envVarModel';
import type { EnvVarFormApi } from './useEnvVarForm';

const ICON: Record<EnvVisibility, string> = { plain: 'visibility', sensitive: 'visibility_off', secret: 'lock' };

/** One radio of the visibility toggle; locked when it would lower an existing variable or make an EXPO_PUBLIC_* secret. */
function VisibilityOption({ v, form }: { v: EnvVisibility; form: EnvVarFormApi }) {
  const selected = form.values.visibility === v;
  const disabled = visibilityLocked(v, form.values.key, form.editing);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      data-testid={`env-var-visibility-${v}`}
      title={VISIBILITY_HELP[v]}
      onClick={() => form.patch({ visibility: v })}
      className={cn('flex h-9 items-center justify-center gap-1.5 rounded-md font-mono text-[13px] transition-colors duration-150 max-sm:h-11', selected ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground', disabled && 'cursor-not-allowed opacity-40')}
    >
      <Icon name={ICON[v]} size={15} />
      {v}
    </button>
  );
}

export function EnvVarVisibility({ form }: { form: EnvVarFormApi }) {
  const error = form.errorFor('visibility');
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[13px]">Visibility</Label>
      <div role="radiogroup" aria-label="Visibility" className="grid grid-cols-3 gap-1 rounded-lg border bg-secondary p-1">
        {VISIBILITIES.map((v) => (
          <VisibilityOption key={v} v={v} form={form} />
        ))}
      </div>
      <p className={cn('text-xs', error ? 'text-destructive' : 'text-subtle')}>{error ?? VISIBILITY_HELP[form.values.visibility]}</p>
    </div>
  );
}
